import React, { useState, useEffect } from 'react';
import { batchApi } from '../../../api/batchApi';
import { BatchExecutionHistory, BATCH_JOB_NAMES, STATUS_ICONS, STATUS_NAMES, STATUS_COLORS } from '../../../types/batch';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { BatchExecutionItemsList } from './BatchExecutionItemsList';

type TabType = 'all' | 'failed' | 'running';

export const BatchHistoryTable: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [history, setHistory] = useState<BatchExecutionHistory[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedHistory, setSelectedHistory] = useState<BatchExecutionHistory | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page]);

  const fetchHistory = async () => {
    try {
      setIsLoading(true);
      let response;

      if (activeTab === 'all') {
        response = await batchApi.getAllBatchHistory(page, 20);
      } else if (activeTab === 'failed') {
        response = await batchApi.getFailedBatchHistory(page, 20);
      } else {
        response = await batchApi.getRunningBatchJobs();
      }

      if (response.data.success) {
        setHistory(response.data.data);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      }
    } catch (error) {
      console.error('이력 조회 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetail = async (historyId: number) => {
    try {
      const response = await batchApi.getBatchHistoryDetail(historyId);
      if (response.data.success) {
        setSelectedHistory(response.data.data);
      }
    } catch (error) {
      console.error('상세 조회 실패:', error);
      alert('상세 정보를 불러오는데 실패했습니다.');
    }
  };

  // 오래된 RUNNING 배치 일괄 정리
  const handleCleanupStuckBatches = async () => {
    if (!window.confirm('오래된 실행 중인 배치를 모두 정리하시겠습니까?')) {
      return;
    }

    try {
      const response = await batchApi.cleanupStuckBatches();
      if (response.data.success) {
        alert(`${response.data.data.cleanedCount}개의 배치를 정리했습니다.`);
        fetchHistory();
      }
    } catch (error: any) {
      alert('배치 정리 실패: ' + (error.response?.data?.message || error.message));
    }
  };

  // 개별 배치 강제 종료
  const handleForceFailBatch = async (historyId: number) => {
    if (!window.confirm('이 배치를 강제 종료하시겠습니까?')) {
      return;
    }

    try {
      const response = await batchApi.forceFailBatch(historyId);
      if (response.data.success) {
        alert('배치를 강제 종료했습니다.');
        fetchHistory();
      }
    } catch (error: any) {
      alert('강제 종료 실패: ' + (error.response?.data?.message || error.message));
    }
  };

  // RUNNING 상태 경과 시간 계산
  const getRunningDuration = (startedAt: string): string => {
    const start = new Date(startedAt);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) return '방금 시작';
    if (diffMinutes < 60) return `${diffMinutes}분 실행 중`;

    const diffHours = Math.floor(diffMinutes / 60);
    return `${diffHours}시간 ${diffMinutes % 60}분 실행 중`;
  };

  // 5분 이상 실행 중인지 확인
  const isStuck = (startedAt: string): boolean => {
    const start = new Date(startedAt);
    const now = new Date();
    const diffMinutes = (now.getTime() - start.getTime()) / 60000;
    return diffMinutes > 5;
  };

  const getStatusBadgeClass = (status: string) => {
    const color = STATUS_COLORS[status as keyof typeof STATUS_COLORS] || 'gray';
    const colorMap: Record<string, string> = {
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
      blue: 'bg-blue-100 text-blue-800',
      orange: 'bg-orange-100 text-orange-800',
      gray: 'bg-gray-100 text-gray-800'
    };
    return colorMap[color];
  };

  return (
    <div className="mt-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>📊 실행 이력</CardTitle>
            {activeTab === 'running' && history.length > 0 && (
              <Button
                variant="outline"
                onClick={handleCleanupStuckBatches}
                className="bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border-yellow-300"
              >
                🧹 오래된 실행중 배치 정리
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* 탭 */}
          <div className="flex gap-2 mb-4 border-b">
            <button
              onClick={() => { setActiveTab('all'); setPage(0); }}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'all'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              전체 이력
            </button>
            <button
              onClick={() => { setActiveTab('failed'); setPage(0); }}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'failed'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              실패만 보기
            </button>
            <button
              onClick={() => { setActiveTab('running'); setPage(0); }}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'running'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              실행 중
            </button>
          </div>

          {/* 테이블 */}
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">로딩 중...</div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {activeTab === 'running' ? '실행 중인 배치가 없습니다.' : '데이터가 없습니다.'}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">배치명</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">시작시간</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">소요시간</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">결과</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">타입</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">상세</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {history.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {BATCH_JOB_NAMES[item.jobType] || item.jobType}
                        </td>
                        <td className="px-4 py-3">
                          {item.status === 'RUNNING' ? (
                            <div className="flex flex-col gap-1">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${isStuck(item.startedAt) ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                                {STATUS_ICONS[item.status]} {STATUS_NAMES[item.status]}
                              </span>
                              <span className="text-xs text-gray-600">
                                {getRunningDuration(item.startedAt)}
                              </span>
                              {isStuck(item.startedAt) && (
                                <span className="text-xs text-red-600">
                                  ⚠️ 비정상 상태
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(item.status)}`}>
                              {STATUS_ICONS[item.status]} {STATUS_NAMES[item.status]}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {new Date(item.startedAt).toLocaleString('ko-KR', {
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {item.durationMs !== null
                            ? item.durationMs < 1000
                              ? `${item.durationMs}ms`
                              : `${(item.durationMs / 1000).toFixed(1)}초`
                            : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {item.resultMessage || (item.status === 'RUNNING' ? '실행 중...' : '-')}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {item.isManualExecution ? '수동' : '자동'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewDetail(item.id)}
                            >
                              보기
                            </Button>
                            {item.status === 'RUNNING' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleForceFailBatch(item.id)}
                                className="bg-red-50 hover:bg-red-100 text-red-700 border-red-300"
                              >
                                ⏹️ 강제 종료
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 페이징 */}
              {activeTab !== 'running' && totalPages > 0 && (
                <div className="flex justify-center items-center gap-4 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    이전
                  </Button>
                  <span className="text-sm text-gray-600">
                    페이지 {page + 1} / {totalPages} (총 {totalElements}건)
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    다음
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* 상세 모달 */}
      {selectedHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedHistory(null)}>
          <div className="bg-white rounded-lg max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-xl font-bold">배치 실행 이력 상세</h3>
                <button
                  onClick={() => setSelectedHistory(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-500">배치명</div>
                    <div className="font-medium">{BATCH_JOB_NAMES[selectedHistory.jobType]}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">상태</div>
                    <div>
                      <span className={`px-2 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(selectedHistory.status)}`}>
                        {STATUS_ICONS[selectedHistory.status]} {STATUS_NAMES[selectedHistory.status]}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">시작 시간</div>
                    <div className="font-medium">
                      {new Date(selectedHistory.startedAt).toLocaleString('ko-KR')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">종료 시간</div>
                    <div className="font-medium">
                      {selectedHistory.finishedAt 
                        ? new Date(selectedHistory.finishedAt).toLocaleString('ko-KR')
                        : '실행 중'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">소요 시간</div>
                    <div className="font-medium">
                      {selectedHistory.durationMs !== null
                        ? selectedHistory.durationMs < 1000
                          ? `${selectedHistory.durationMs}ms`
                          : `${(selectedHistory.durationMs / 1000).toFixed(1)}초`
                        : '-'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">실행 타입</div>
                    <div className="font-medium">
                      {selectedHistory.isManualExecution ? '수동 실행' : '자동 실행'}
                      {selectedHistory.executedBy && ` (${selectedHistory.executedBy})`}
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="text-sm text-gray-500 mb-2">처리 결과</div>
                  <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4 rounded">
                    <div>
                      <div className="text-xs text-gray-500">전체</div>
                      <div className="text-lg font-bold">{selectedHistory.totalCount}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">성공</div>
                      <div className="text-lg font-bold text-green-600">{selectedHistory.successCount}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">실패</div>
                      <div className="text-lg font-bold text-red-600">{selectedHistory.failedCount}</div>
                    </div>
                  </div>
                </div>

                {selectedHistory.resultMessage && (
                  <div>
                    <div className="text-sm text-gray-500 mb-2">결과 메시지</div>
                    <div className="bg-gray-50 p-3 rounded text-sm whitespace-pre-line">{selectedHistory.resultMessage}</div>
                  </div>
                )}

                {selectedHistory.errorMessage && (
                  <div>
                    <div className="text-sm text-red-600 mb-2">에러 메시지</div>
                    <div className="bg-red-50 p-3 rounded text-sm text-red-800 whitespace-pre-line">
                      {selectedHistory.errorMessage}
                    </div>
                  </div>
                )}

                {selectedHistory.errorStackTrace && (
                  <div>
                    <div className="text-sm text-red-600 mb-2">스택 트레이스</div>
                    <pre className="bg-red-50 p-3 rounded text-xs text-red-800 overflow-x-auto">
                      {selectedHistory.errorStackTrace}
                    </pre>
                  </div>
                )}

                <BatchExecutionItemsList historyId={selectedHistory.id} />
              </div>

              <div className="mt-6 flex justify-end">
                <Button onClick={() => setSelectedHistory(null)}>
                  닫기
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
